import type { CASLRule } from "@iotos/ims-auth-sdk/permissions";
import { mapStringPermissions } from "@iotos/ims-auth-sdk/permissions";
import { createDraftSafeSelector } from "@reduxjs/toolkit";

import { RootState } from "../../store-types";
import { IAuthState } from "../shared/interfaces/auth-state";

import { AUTH_MODULE_NAME } from "./constants";

export const getAuthState = (rootState: RootState): IAuthState =>
  rootState[AUTH_MODULE_NAME];

export const getUser = createDraftSafeSelector(
  getAuthState,
  (state: IAuthState) => state.user
);

// TODO(IMS): BFF отдаёт `/ims/me` в snake_case (`user_id`). Убрать откат, когда вернут `userId`.
export const getUserId = createDraftSafeSelector(
  getUser,
  (user) => user?.userId ?? (user as { user_id?: string } | null)?.user_id ?? null
);

export const getIsAuthenticated = createDraftSafeSelector(
  getAuthState,
  (state: IAuthState) => state.isAuthenticated
);

export const getLoadingUserDto = createDraftSafeSelector(
  getAuthState,
  (state: IAuthState) => state.isLoadingUserDto
);

export const getError = createDraftSafeSelector(
  getAuthState,
  (state: IAuthState) => state.error
);

export const getSessionCheckFailure = createDraftSafeSelector(
  getAuthState,
  (state: IAuthState) => state.sessionCheckFailure
);

/**
 * `allowView` → `view`, `allowEdit` → `edit`. Флаг без префикса берётся как есть.
 */
const featureFlagToAction = (flag: string): string =>
  flag.startsWith("allow") ? flag.charAt(5).toLowerCase() + flag.slice(6) : flag;

/**
 * Права в контракте IMS приходят двумя способами: строками `"subject:action"`
 * в `permissions` и вложенной картой флагов в `features` (группа → флаг → включён).
 * SDK разбирает только строки и про второе поле прямо пишет, что «never reads this
 * field, it only carries it through» (`dist/auth/types.d.ts`), поэтому карту
 * раскладываем сами — по той же уравниловке: группа становится subject, флаг — action.
 *
 * ekocrop-srv на 2026-09-22 отдаёт EkoCrop именно `features`
 * (`{ web: { allowView, allowEdit }, mobile: {...} }`), а `permissions` шлёт пустым.
 */
export const mapFeatureRules = (
  features?: Record<string, Record<string, boolean>>
): CASLRule[] =>
  Object.entries(features ?? {}).flatMap(([subject, flags]) =>
    Object.entries(flags ?? {})
      .filter(([, isAllowed]) => isAllowed)
      .map(([flag]) => ({ action: featureFlagToAction(flag), subject }))
  );

/** Оба источника прав, приведённые к правилам CASL. */
export const getPermissionRules = createDraftSafeSelector(getUser, (user) => [
  ...mapStringPermissions(user?.permissions ?? []),
  ...mapFeatureRules(user?.features),
]);
